import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  analyzeWordBankWord,
  addWordBankEntry,
  synthesizeSpeech,
  isAudioPlaybackAllowed,
} from "../api";
import { track } from "../utils/analytics";

/**
 * AddWordPopover — a small tooltip for adding a single raw Arabic word to the
 * user's Word Bank. Styled and positioned exactly like the scenario WordBubble:
 * a compact dark bubble that sits above the tapped word (dropping below only
 * when there isn't room), clamped to the viewport so it works on mobile.
 *
 * The bookmark button adds the word via the text path of POST /api/word-bank/.
 * Translation is an explicit action (the "Translate" button) so a tap never
 * spends an AI lookup on its own — the daily AI word-lookup cap only ticks
 * when the learner actually asks for a meaning. Opening does run a FREE cache
 * probe (server WordTranslationCache): a word anyone already translated —
 * standalone or inside a sentence translation — shows its meaning instantly.
 *
 * Used wherever we only have the word as a string (no Word id): the Reading
 * OCR / reading overlay and the chat bubbles. Surfaces that already have a
 * Word id (scenario tooltip, vocabulary step) call addWordBankEntry({ word_id })
 * directly instead.
 *
 * Props:
 *   word       — the raw Arabic string to add
 *   anchorRect — the tapped word's getBoundingClientRect() (viewport coords)
 *   onClose    — called to dismiss
 *   prefill    — optional cached gloss {definition, transliteration, pos}
 *                (e.g. from a sentence translation): shown immediately and the
 *                AI translate button is skipped — no lookup is spent.
 *   mode       — "app" (default) | "public": on the public /reader page the
 *                Word Bank bookmark and TTS are account features, so they're
 *                replaced by a small signup link. Probe + AI translate still
 *                work (free tier, server-capped).
 */
export default function AddWordPopover({ word, anchorRect, onClose, prefill = null, mode = "app" }) {
  const isPublic = mode === "public";
  const ref = useRef(null);
  const [place, setPlace] = useState(null); // { left, top } | null (hidden until measured)
  const [pos, setPos] = useState(prefill?.pos || "noun");
  const [definition, setDefinition] = useState(prefill?.definition || "");
  const [transliteration, setTransliteration] = useState(prefill?.transliteration || "");
  const [analysis, setAnalysis] = useState(prefill?.definition ? { ...prefill } : null); // full AI result (incl. number/counterpart)
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(!!prefill?.definition);
  const [error, setError] = useState(null);
  const [wbState, setWbState] = useState("idle"); // idle | adding | added | exists | error

  // Run the AI lookup and reflect it in the UI; returns the raw result.
  const runTranslate = async () => {
    const r = await analyzeWordBankWord(word);
    setDefinition(r.definition || "");
    setTransliteration(r.transliteration || "");
    if (r.pos) setPos(r.pos);
    setAnalysis(r);
    setTranslated(true);
    return r;
  };

  // On open (no prefill): free cache probe — a word anyone translated before
  // (standalone or inside a sentence translation) shows its meaning at once;
  // a miss just leaves the explicit Translate button as before.
  useEffect(() => {
    if (prefill?.definition) return;
    let alive = true;
    analyzeWordBankWord(word, { probe: true })
      .then((r) => {
        if (!alive || !r.cached || !r.definition) return;
        setDefinition(r.definition);
        setTransliteration(r.transliteration || "");
        if (r.pos) setPos(r.pos);
        setAnalysis(r);
        setTranslated(true);
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word]);

  // AI translate — explicit, so it only spends a lookup when the user asks.
  const translate = async () => {
    if (translating || translated) return;
    setTranslating(true);
    setError(null);
    try {
      await runTranslate();
    } catch (e) {
      setError(e.message || "Could not translate.");
    } finally {
      setTranslating(false);
    }
  };

  // Dismiss on outside tap / Escape / scroll — identical to WordBubble.
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  // Measure, then centre over the word and flip above/below. We position in
  // PAGE coordinates (client rect + scroll) with position:absolute rather than
  // fixed: an absolute element is part of the zoomable page, so mobile
  // pinch-zoom magnifies and pans it *together with the word* and it stays
  // anchored. A fixed element is glued to the layout viewport and drifts off
  // the word when pinch-zoomed. Horizontal clamp keeps it within the page's
  // layout width.
  const reposition = useCallback(() => {
    const el = ref.current;
    if (!el || !anchorRect) return;
    const W = el.offsetWidth;
    const H = el.offsetHeight;
    const MARGIN = 8;
    const sx = window.scrollX || window.pageXOffset || 0;
    const sy = window.scrollY || window.pageYOffset || 0;

    const centerX = anchorRect.left + anchorRect.width / 2 + sx;
    const minLeft = sx + MARGIN;
    const maxLeft = sx + window.innerWidth - W - MARGIN;
    const left = maxLeft > minLeft
      ? Math.max(minLeft, Math.min(maxLeft, centerX - W / 2))
      : minLeft;
    const arrowLeft = Math.max(8, Math.min(W - 20, centerX - left)) - 4;

    const showAbove = anchorRect.top > H + 20;
    const top = showAbove ? anchorRect.top + sy - H - 10 : anchorRect.bottom + sy + 10;

    setPlace({ left, top, showAbove, arrowLeft });
  }, [anchorRect]);

  useLayoutEffect(() => { reposition(); }, [reposition]);

  // Content height changes once the translation resolves — re-anchor on resize.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => reposition());
    ro.observe(el);
    return () => ro.disconnect();
  }, [reposition]);

  const add = async () => {
    if (wbState === "adding" || wbState === "added" || wbState === "exists") return;
    setWbState("adding");
    setError(null);
    try {
      // Translate first if we haven't yet, so the word is saved with a meaning
      // and its singular/plural counterpart can be attached. If the lookup
      // fails (e.g. daily cap), fall back to adding the bare word.
      let r = analysis;
      if (!translated) {
        try { r = await runTranslate(); } catch { r = null; }
      }
      const res = await addWordBankEntry({
        text: word,
        pos: r?.pos || pos,
        definition: (r?.definition ?? definition).trim(),
        transliteration: (r?.transliteration ?? transliteration).trim(),
        number: r?.number || "",
        counterpart: r?.counterpart || "",
        counterpart_transliteration: r?.counterpart_transliteration || "",
      });
      setWbState(res?.already_in_bank ? "exists" : "added");
    } catch (e) {
      setWbState("error");
      setError(e.message || "Could not add the word.");
    }
  };

  const done = wbState === "added" || wbState === "exists";
  const posStyle = place
    ? { position: "absolute", left: place.left, top: place.top, zIndex: 9999 }
    : { position: "absolute", left: 0, top: 0, visibility: "hidden", zIndex: 9999 };

  return ReactDOM.createPortal(
    <div ref={ref} style={posStyle} className="bg-slate-800 text-white rounded-lg shadow-2xl px-3 py-2 max-w-[280px] min-w-[100px]">
      <div className="flex items-center gap-2">
        <p className="flex-1 text-sm leading-snug whitespace-normal">
          {translating ? "Translating…" : (definition || <span dir="rtl">{word}</span>)}
        </p>

        {/* Translate (AI) — explicit so it only costs a lookup on request */}
        {!translated && (
          <button
            type="button"
            title="Translate (AI)"
            aria-label="Translate with AI"
            onClick={translate}
            disabled={translating}
            className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-violet-300 transition-colors disabled:opacity-40"
          >
            {translating ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            )}
          </button>
        )}

        {/* Play (hidden when TTS is blocked, e.g. seatless institute admin,
            and on the public page where TTS needs an account) */}
        {!isPublic && isAudioPlaybackAllowed() && (
          <button
            type="button"
            title="Play"
            onClick={async () => {
              try {
                const d = await synthesizeSpeech(word, "");
                new Audio(`data:audio/mp3;base64,${d.audio_content}`).play().catch(() => {});
              } catch { /* ignore */ }
            }}
            className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-emerald-400 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M6 4l14 8-14 8V4z" /></svg>
          </button>
        )}

        {/* Add to Word Bank (needs an account — the public page links signup) */}
        {!isPublic && (
        <button
          type="button"
          onClick={add}
          disabled={wbState === "adding"}
          title={
            wbState === "added" ? "Added to your Word Bank"
            : wbState === "exists" ? "Already in your Word Bank"
            : wbState === "error" ? "Could not add — tap to retry"
            : "Add to Word Bank"
          }
          aria-label="Add to Word Bank"
          className={`shrink-0 p-0.5 rounded-full transition-colors disabled:opacity-40 ${
            done ? "text-emerald-400"
            : wbState === "error" ? "text-red-400 hover:text-red-300"
            : "text-slate-400 hover:text-emerald-400"
          }`}
        >
          {wbState === "adding" ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : done ? (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 2h12a1 1 0 011 1v18l-7-4-7 4V3a1 1 0 011-1z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 3h12a1 1 0 011 1v17l-7-4-7 4V4a1 1 0 011-1z" />
            </svg>
          )}
        </button>
        )}
      </div>

      {isPublic && (
        <a
          href="/?signup=1"
          onClick={() => track("reader-signup-click", { kind: "popover" })}
          className="block text-[11px] text-slate-400 hover:text-emerald-400 mt-1 no-underline"
        >
          Save words — free account →
        </a>
      )}

      {transliteration && translated && (
        <p className="text-[11px] text-slate-400 italic mt-0.5" dir="ltr">{transliteration}</p>
      )}
      {error && <p className="text-[11px] text-red-400 mt-0.5">{error}</p>}

      {/* Arrow pointing to the word — matches the scenario tooltip */}
      {place && (
        <div
          className={`absolute w-2.5 h-2.5 bg-slate-800 rotate-45 ${place.showAbove ? "bottom-[-5px]" : "top-[-5px]"}`}
          style={{ left: place.arrowLeft }}
        />
      )}
    </div>,
    document.body
  );
}
